/**
 * EntityLink — renders an entity mention on a /data page as a real <Link>
 * when lib/data/entity-links.ts resolved a live page for it, and as plain text
 * when it did not (FRIDGE-334: every data mention that names an entity with a
 * live page is clickable; nothing ever links to a gated or missing page).
 *
 * Works in server AND client components: `link` is a plain serializable
 * object (href + label), so the server resolves once and the client tables
 * receive the result as a prop. Light styling only, house palette.
 */
import Link from 'next/link'
import type { ReactNode } from 'react'
import type { EntityLink as EntityLinkTarget } from '@/lib/data/entity-links'

/** Serializable shape the tables carry per row. */
export type LinkTarget = Pick<EntityLinkTarget, 'href' | 'label'> | null | undefined

interface EntityLinkProps {
  link: LinkTarget
  children: ReactNode
  className?: string
  /** Visual style: 'text' (underline on hover, default) or 'chip' (pill). */
  variant?: 'text' | 'chip'
  /** Optional accessible title, defaults to "Browse <destination title>". */
  title?: string
}

// `entity-link`: inside a table cell (one nowrap line) the link taps as 24px
// tall, from app/globals.css (FRIDGE-1507 T); in running text it stays a plain
// inline link, which WCAG 2.5.8 exempts.
const TEXT =
  'entity-link text-gray-900 underline decoration-[#0469f0]/40 decoration-1 underline-offset-[3px] hover:decoration-[#0469f0] hover:text-[#0469f0] transition-colors'
const CHIP =
  'inline-flex items-center gap-1 border border-[#ff9951]/40 text-[#b25410] hover:border-[#ff9951] hover:bg-[#ff9951]/5 rounded-full px-2.5 py-0.5 text-[12px] font-semibold transition-colors'

export function EntityLink({ link, children, className = '', variant = 'text', title }: EntityLinkProps) {
  if (!link) return className ? <span className={className}>{children}</span> : <>{children}</>
  const base = variant === 'chip' ? CHIP : TEXT
  return (
    <Link
      href={link.href}
      prefetch={false}
      className={`${base} ${className}`}
      title={title ?? `Browse ${link.label} on Fridge.com`}
    >
      {children}
    </Link>
  )
}
